<template>
    <section class="flex flex-col gap-5">
        <header class="flex flex-col gap-2">
            <div class="flex items-center gap-3 flex-wrap">
                <h2 class="title-subsection text-valence-stone-900">Admin</h2>
                <BaseButton theme="neutral" size="sm" class="base-btn-square ml-auto" @click="emit('settings')">
                    <i class="bi bi-gear" aria-hidden="true"></i>
                    Settings
                </BaseButton>
                <!-- LAB-988 dark CTA override — see .base-btn-dark in tailwind.css. -->
                <BaseButton theme="primary" size="sm" class="base-btn-dark" :disabled="templates.length === 0" @click="emit('new-campaign', templates)">
                    <i class="bi bi-plus-lg" aria-hidden="true"></i>
                    New campaign
                </BaseButton>
            </div>
            <p class="body-small text-valence-stone-500">Organization-wide campaign insights and reporting.</p>
        </header>

        <hr class="border-t border-valence-cream-300" />

        <div v-if="loading" class="body-small text-valence-stone-500">Loading…</div>
        <BaseNotification v-else-if="loadError" theme="error" :text="loadError" hide-dismiss />

        <template v-else>
            <div class="flex flex-col gap-2">
                <div class="scaffold-details-small text-valence-stone-500">Campaigns</div>
                <BaseCard v-if="activeCampaigns.length === 0" padding="md" class="body-small text-valence-stone-500">
                    No active campaigns. Create one to roll out an assessment to everyone in your organization.
                </BaseCard>
                <AdminCampaignRow
                    v-for="campaign in activeCampaigns"
                    :key="campaign.id"
                    :campaign="campaign"
                    :busy="transitioningId === campaign.id"
                    @launch="onLaunch"
                    @close="onClose"
                    @view="onView" />
            </div>

            <div v-if="closedCampaigns.length" class="flex flex-col gap-2 mt-5">
                <div class="scaffold-details-small text-valence-stone-500">Previous Reflections</div>
                <AdminCampaignRow v-for="campaign in closedCampaigns" :key="campaign.id" :campaign="campaign" :busy="false" @view="onView" />
            </div>

            <BaseNotification v-if="actionStatus" :theme="actionStatus.theme" :text="actionStatus.text" @dismiss="actionStatus = null" />
        </template>

        <AssessmentDemoTools :campaign-actions="campaignDemoActions" />
    </section>
</template>

<script setup lang="ts">
import { useFetch } from "~vue/api";
import type { AdminCampaignResponse, AdminCampaignSummary, AdminHubPayload, TemplateSummary } from "~vue/api/generated/reflections-admin";
import BaseButton from "~vue/components/BaseButton.vue";
import BaseCard from "~vue/components/BaseCard.vue";
import BaseNotification from "~vue/components/BaseNotification.vue";
import AdminCampaignRow from "~vue/pages/reflections/AdminCampaignRow.vue";
import AssessmentDemoTools from "~vue/pages/reflections/AssessmentDemoTools.vue";
import { computed, onMounted, ref } from "vue";

const campaigns = ref<AdminCampaignSummary[]>([]);
const templates = ref<TemplateSummary[]>([]);
const loading = ref(true);
const loadError = ref<string | null>(null);
const transitioningId = ref<string | null>(null);
const actionStatus = ref<{ theme: "success" | "error"; text: string } | null>(null);

const emit = defineEmits<{
    (event: "new-campaign", templates: TemplateSummary[]): void;
    (event: "view-campaign", campaignId: string): void;
    (event: "settings"): void;
}>();

const activeCampaigns = computed(() => campaigns.value.filter((c) => c.state !== "closed"));
const closedCampaigns = computed(() => campaigns.value.filter((c) => c.state === "closed"));
const campaignDemoActions = computed(() => activeCampaigns.value.map((campaign) => ({ id: campaign.id, label: campaign.name })));

onMounted(async () => {
    const { data, error: fetchError } = await useFetch("/v1/reflections/admin/campaigns").json<AdminHubPayload>();
    loading.value = false;
    if (fetchError.value) {
        loadError.value = fetchError.value.message || "Could not load campaigns.";
        return;
    }
    campaigns.value = data.value?.campaigns ?? [];
    templates.value = data.value?.templates ?? [];
});

function onView(campaignId: string) {
    emit("view-campaign", campaignId);
}

async function transitionCampaign(campaign: AdminCampaignSummary, action: "launch" | "close") {
    transitioningId.value = campaign.id;
    actionStatus.value = null;
    const { data, error: fetchError } = await useFetch(`/v1/reflections/admin/campaigns/${campaign.id}/${action}`, { method: "POST" }).json<AdminCampaignResponse>();
    transitioningId.value = null;
    if (fetchError.value) {
        actionStatus.value = { theme: "error", text: (data.value as { error?: string } | null)?.error || `Could not ${action} the campaign.` };
        return;
    }
    if (data.value) {
        campaigns.value = campaigns.value.map((existing) => (existing.id === campaign.id ? (data.value as AdminCampaignResponse).campaign : existing));
    }
    actionStatus.value = {
        theme: "success",
        text: action === "launch" ? `Launched "${campaign.name}" — it's now visible to everyone in your organization.` : `Closed "${campaign.name}".`,
    };
}

async function onLaunch(campaign: AdminCampaignSummary) {
    if (!globalThis.confirm(`Launch "${campaign.name}"? It becomes visible to everyone in your organization.`)) {
        return;
    }
    await transitionCampaign(campaign, "launch");
}

async function onClose(campaign: AdminCampaignSummary) {
    if (!globalThis.confirm(`Close "${campaign.name}"? This also closes all participant cycles and can't be undone.`)) {
        return;
    }
    await transitionCampaign(campaign, "close");
}
</script>
